import React, { useState, useEffect } from "react";
import {
  Rocket,
  Send,
  ArrowLeft,
  Bell,
  Activity,
  Cpu,
  Zap,
  Globe,
  Timer,
  ChevronRight,
  ShieldCheck,
  Target
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import FooterSection from "../components/FooterSection";
import "./ComingSoon.css";

const ComingSoon = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Calculate countdown
  useEffect(() => {
    // UPDATED LAUNCH DATE: June 1, 2026
    const launchDate = new Date("2026-05-01T00:00:00").getTime();

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = launchDate - now;

      if (distance < 0) {
        clearInterval(timer);
        navigate("/");
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail("");
    }
  };

  const upcomingFeatures = [
    { title: "Advanced Vitals AI", desc: "Predictive diagnostics using machine learning kernels.", icon: <Cpu size={24} />, code: "KERNEL_P_7" },
    { title: "Global Telemetry Hub", desc: "Low-latency data synchronization across 12 regions.", icon: <Globe size={24} />, code: "SYNC_PROTO_X" },
    { title: "Security Matrix 2.0", desc: "AES-512 hardware-level encryption nodes.", icon: <ShieldCheck size={24} />, code: "CYPHER_GRID" },
  ];

  return (
    <div className="csc-page-shell">
      <Navigation />

      {/* Sovereign Visual Overlays */}
      <div className="csc-tactical-grid"></div>
      <div className="csc-scanline-overlay"></div>
      <div className="csc-laser-scanner"></div>
      <div className="csc-radial-depth"></div>

      <main className="csc-content-hub">

        {/* Sector 1: Hero Node */}
        <header className="csc-hero-sector">
          <div className="csc-badge-container">
            <div className="csc-hud-badge">
              <Rocket size={14} className="csc-icon-cyan csc-pulse" />
              <span>PHASE_02_ENGAGEMENT</span>
            </div>
          </div>

          <h1 className="csc-hero-title">
            WE'RE BUILDING THE <br />
            <span className="csc-highlight">FUTURE OF HEALTHCARE</span>
          </h1>

          <p className="csc-hero-subtitle">
            This module is currently undergoing high-fidelity optimization. We are meticulous about every clinical node to ensure the highest standards of medical precision.
          </p>

          {/* Countdown Grid (Tactical Units) */}
          <div className="csc-countdown-grid">
            {Object.entries(timeLeft).map(([label, value]) => (
              <div key={label} className="csc-countdown-item csc-glass">
                <span className="csc-count-value">{value.toString().padStart(2, '0')}</span>
                <span className="csc-count-label">{label.toUpperCase()}</span>
                <div className="csc-card-brackets"></div>
              </div>
            ))}
          </div>

          {/* Progress Architecture */}
          <div className="csc-progress-protocol">
            <div className="csc-progress-header">
              <span className="csc-label">DEVELOPMENT_POSTURE</span>
              <span className="csc-value">85% COMPLETE</span>
            </div>
            <div className="csc-progress-rail csc-glass">
              <div className="csc-progress-fill">
                <div className="csc-progress-scanline"></div>
              </div>
            </div>
          </div>
        </header>

        {/* Sector 2: Subscription Relay */}
        <section className="csc-relay-sector">
          <div className="csc-relay-card csc-glass">
            {!subscribed ? (
              <form className="csc-relay-form" onSubmit={handleSubmit}>
                <div className="csc-relay-header">
                  <Bell size={24} className="csc-icon-blue" />
                  <h3>INITIALIZE_NOTIFICATION_PROTOCOL</h3>
                  <p>Secure an early synchronization slot for this feature deployment.</p>
                </div>
                <div className="csc-input-matrix">
                  <input
                    type="email"
                    className="csc-input-node"
                    placeholder="ENTER_IDENTITY_EMAIL"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button type="submit" className="csc-premium-btn">
                    <span>SYNCHRONIZE</span>
                    <ChevronRight size={18} />
                  </button>
                </div>
              </form>
            ) : (
              <div className="csc-success-relay">
                <div className="csc-success-icon-hub">
                  <Send size={32} className="csc-icon-green" />
                </div>
                <h3>IDENTITY_SYNC_SUCCESS</h3>
                <p>Your clinical node has been queued for launch notification.</p>
                <button
                  className="csc-reset-relay"
                  onClick={() => setSubscribed(false)}
                >
                  QUEUE_ANOTHER_IDENTITY
                </button>
              </div>
            )}
            <div className="csc-card-laser"></div>
          </div>
        </section>

        {/* Sector 3: Upcoming Node Matrix */}
        <section className="csc-node-matrix">
          <div className="csc-sector-header">
            <h2 className="csc-sector-title">UPCOMING_CLINICAL_NODES</h2>
          </div>

          <div className="csc-feature-grid">
            {upcomingFeatures.map((feature, idx) => (
              <div key={idx} className="csc-feature-card csc-glass">
                <div className="csc-feature-header">
                  <div className="csc-feature-icon">{feature.icon}</div>
                  <div className="csc-feature-code">{feature.code}</div>
                </div>
                <h4>{feature.title}</h4>
                <p>{feature.desc}</p>
                <div className="csc-card-brackets"></div>
              </div>
            ))}
          </div>
        </section>

      </main>
      <FooterSection />
    </div>
  );
};

export default ComingSoon;
